/**
 * Lobby. SPEC.md §33, §34.1, §27.1.
 *
 * Everything a table needs before the first hand: the code to read out, something
 * to scan, who has actually arrived, where they are sitting, whether the table is
 * still open, and the button that deals. §33
 *
 * The first hand starts the same way every later hand does — one tap that says
 * nothing except "deal", because what the hand means is decided afterwards by
 * what gets reported into it. And it is not a host power, so it is the same
 * button for everybody. §3, §4.3, §5.1
 *
 * The ring preview uses the shared ringLayout, exactly as the table screen does,
 * so the seat someone sees here is the seat they get. §9.2
 */

import { useId, useMemo, useState } from 'react';
import {
  MIN_PLAYERS_TO_START,
  avatarScaleForCount,
  formatJoinCode,
  messageForErrorCode,
  ringLayout,
  truncateName,
} from '@card-counter/shared';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { useSession } from '@/hooks/useSession';
import {
  useIsHost,
  useMe,
  usePlayersBySeat,
  useSessionInfo,
} from '@/stores/session-store';
import { ShareCard } from './ShareCard';

export function LobbyScreen() {
  const session = useSessionInfo();
  const players = usePlayersBySeat();
  const me = useMe();
  const isHost = useIsHost();
  const { interactive, setLocked, startHand } = useSession();

  const [lockPending, setLockPending] = useState(false);
  const [starting, setStarting] = useState(false);
  const startHintId = useId();

  const mySeatIndex = me?.seatIndex ?? null;
  const slots = useMemo(
    () =>
      ringLayout({
        seatIndexes: players.map((player) => player.seatIndex),
        // The viewer sits at the bottom of their own phone, here as at the table. §9.2
        viewerSeatIndex: mySeatIndex,
      }),
    [players, mySeatIndex],
  );

  // Nothing to show until the first snapshot lands; the table screen owns that
  // waiting state. §16.3
  if (!session) return null;

  const bySeat = new Map(players.map((player) => [player.seatIndex, player]));
  const avatarScale = avatarScaleForCount(players.length);
  const enoughPlayers = players.length >= MIN_PLAYERS_TO_START;

  const toggleLock = async (): Promise<void> => {
    setLockPending(true);
    // Host powers are checked server-side; this only reflects the answer. §27, §39
    await setLocked(!session.locked);
    setLockPending(false);
  };

  const start = async (): Promise<void> => {
    setStarting(true);
    // One tap, and it carries nothing: what the hand means is decided by what
    // gets reported into it. §3, §4.3
    await startHand();
    setStarting(false);
  };

  const canStart = enoughPlayers && interactive;

  return (
    <main className="flex min-h-0 flex-1 flex-col">
      <div className="scroll-area flex-1">
        <div className="mx-auto flex w-full max-w-sm flex-col gap-5 p-safe">
          <header className="flex flex-col gap-1 text-center">
            <h1 className="text-lg font-semibold text-ink">Lobby</h1>
            <p className="text-sm text-ink-muted">
              {players.length === 1 ? '1 player' : `${String(players.length)} players`} at{' '}
              <span data-numeric>{formatJoinCode(session.joinCode)}</span>
            </p>
          </header>

          <ShareCard />

          {/* Decorative: the list below is the accessible version of the same thing. */}
          <div
            aria-hidden="true"
            className="relative mx-auto aspect-square w-full max-w-[17rem] rounded-full border border-line"
          >
            {slots.map((slot) => {
              const player = bySeat.get(slot.seatIndex);
              if (!player) return null;
              return (
                <div
                  key={player.id}
                  style={{ left: `${String(slot.xPct)}%`, top: `${String(slot.yPct)}%` }}
                  className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1"
                >
                  <span
                    style={{
                      width: `calc(var(--avatar-size) * ${String(avatarScale)})`,
                      height: `calc(var(--avatar-size) * ${String(avatarScale)})`,
                    }}
                    className={[
                      'grid place-items-center rounded-full border bg-raised',
                      player.presence === 'offline' ? 'border-line opacity-50' : 'border-line',
                      player.id === me?.id ? 'border-accent' : '',
                    ].join(' ')}
                  >
                    <Avatar avatarKey={player.avatarKey} className="size-[86%]" />
                  </span>
                  <span className="max-w-[6.5rem] truncate text-xs text-ink-muted">
                    {truncateName(player.name)}
                  </span>
                </div>
              );
            })}
            <div className="absolute inset-0 grid place-items-center">
              <span className="text-sm text-ink-faint">
                {players.length === 1 ? '1 player' : `${String(players.length)} players`}
              </span>
            </div>
          </div>

          <section aria-label="Players" className="flex flex-col gap-2">
            <ul className="flex flex-col gap-2">
              {players.map((player) => {
                const rowClass =
                  'flex min-h-tap w-full items-center gap-3 rounded-card border border-line bg-surface px-3 py-2 text-left';

                const body = (
                  <>
                    <Avatar avatarKey={player.avatarKey} className="size-8 shrink-0" />
                    <span className="min-w-0 flex-1 truncate text-ink">
                      {truncateName(player.name)}
                    </span>
                    {player.id === me?.id ? (
                      // §8.2 — a small marker, not a different size.
                      <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold tracking-wide text-accent uppercase">
                        You
                      </span>
                    ) : null}
                    {player.isHost ? (
                      <span className="rounded-full bg-raised px-2 py-0.5 text-xs font-semibold tracking-wide text-ink-muted uppercase">
                        <span aria-hidden="true">👑 </span>Host
                      </span>
                    ) : null}
                    {/* Offline players stay at the table until the host removes them. §8.5 */}
                    {player.presence === 'offline' ? (
                      <span className="text-xs text-ink-faint">Offline</span>
                    ) : null}
                  </>
                );

                return (
                  <li key={player.id}>
                    <div className={rowClass}>{body}</div>
                  </li>
                );
              })}
            </ul>

            {/*
              Anyone may deal, so this is one button rather than a control the
              rest of the table waits on somebody else to press. §5.1
            */}
            <Button
              size="lg"
              fullWidth
              loading={starting}
              disabled={!canStart}
              aria-describedby={enoughPlayers ? undefined : startHintId}
              onClick={() => {
                void start();
              }}
            >
              Deal the first hand
            </Button>

            {enoughPlayers ? (
              <p className="text-center text-xs text-ink-faint">
                Everyone reports what they lost. Whoever reports nothing wins the hand.
              </p>
            ) : (
              <p id={startHintId} role="status" className="text-center text-sm text-warn">
                {/* §34.1 */}
                {messageForErrorCode('NOT_ENOUGH_PLAYERS')}
              </p>
            )}
          </section>

          <div className="flex flex-col gap-2">
            <p className="text-center text-sm text-ink-muted">
              {session.locked ? (
                <>
                  <span aria-hidden="true">🔒 </span>Locked — no new players
                </>
              ) : (
                <>
                  <span aria-hidden="true">🔓 </span>Open — anyone with the code can join
                </>
              )}
            </p>

            {isHost ? (
              <Button
                variant="secondary"
                fullWidth
                loading={lockPending}
                disabled={!interactive}
                onClick={() => {
                  void toggleLock();
                }}
              >
                {session.locked ? 'Unlock Table' : 'Lock Table'}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
}
